import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';

import { ChatbotRTKService } from '../../app/services/chatbot-rtk.services';
import { ScenarioRTKService } from '../../app/services/scenario-rtk.service';
import { RootState } from '../../app/store';
import { BotCategoryEnum } from '../../utils/BotCategoryEnum';
import { BotTypeEnum } from '../../utils/BotTypeEnum';
import { AIKittyType, Assistant, Bot, BotCode, BotType, BotUIInfo, BotVersion, BotWizard, CdnFileType, McBotFaceType } from './types';

const versionAdapter = createEntityAdapter<BotVersion>({
  selectId: model => model.code,
});

const assistantAdapter = createEntityAdapter<Assistant>({
  selectId: model => model.code,
});

const botAdapter = createEntityAdapter<Bot>({
  selectId: model => model.id,
  sortComparer: (a, b) => new Date(b.creation_date_time).getTime() - new Date(a.creation_date_time).getTime(),
});

export type InitialState = {
  versions: EntityState<BotVersion>;
  assistants: EntityState<Assistant>;
  clientBots?: EntityState<Bot>;
  wizard: BotWizard;
  ui: {
    showIntegrationModal: boolean;
    integrationModalData: {
      botId: string;
      title: string;
      botCode: BotCode | null;
    };
    showCloneShareModal: boolean;
    cloneShareModalData: {
      cloneId: string;
      logo: string;
    };
    livePreviewTargetBot: string;
  };
};

type initialBotConfigType = {
  McBotFace: McBotFaceType;
  AIKitty: AIKittyType;
};

const initialState: InitialState = {
  versions: versionAdapter.getInitialState(),
  assistants: assistantAdapter.getInitialState(),
  // clientBots: botAdapter.getInitialState(),
  wizard: {
    title: '',
    showVisibilityModal: false,
    category: 'widget',
    id: '',
    bot_code: '',
    bot_type: '',
    bot_config: null,
    bot_ui_info: {
      wizard: null,
      currentStep: 1,
    },
    isInEditMode: false,
    showModal: false,
  },
  ui: {
    showIntegrationModal: false,
    integrationModalData: {
      botId: '',
      title: '',
      botCode: null,
    },
    showCloneShareModal: false,
    cloneShareModalData: {
      cloneId: '',
      logo: '',
    },
    livePreviewTargetBot: '',
  },
};

const initialBotConfig: initialBotConfigType = {
  McBotFace: {
    bot_code: '',
    title: '',
    template_id: null,
  },
  AIKitty: {
    bot_code: '',
    maxToken: 1000,
    configuration: 'FAST_SMART',
    additional_instructions: [],
    assistant: '',
    instructions: {},
    title: '',
    welcoming: '',
  },
};

const initialWizardConfig: { edit: BotUIInfo; create: BotUIInfo } = {
  edit: {
    KITTY_02: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key1',
        component: 'AssistantTemplates',
      },
      step3: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
        component: 'AiAdditionalInfo',
      },
      step4: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
        component: 'ReactionSpeed',
      },
    },
    KITTY_03: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key4',
        component: 'AssistantTemplates',
      },
      step3: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
        component: 'AiAdditionalInfo',
      },
      step4: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key6',
        component: 'ReactionSpeed',
      },
      step5: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key5',
        component: 'KnowledgeSeeder',
      },
    },
    KITTY_04: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key10',
        component: 'PlugAndPlayDomain',
      },
      step3: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key14',
        component: 'PlugAndPlayDomainLinks',
      },
      step4: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key11',
        component: 'PlugAndPlayAutoUpdate',
      },
    },
    MCBOT_01: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key8',
        component: 'TextScenarioTemplates',
      },
    },
    VIDEO_SCENARIO: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key9',
        component: 'VideoScenarioTemplates',
      },
    },
    CLONE_01: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
        component: 'AiAdditionalInfo',
      },
      step3: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
        component: 'ReactionSpeed',
      },
      step4: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key5',
        component: 'KnowledgeSeeder',
      },
    },
  },
  create: {
    KITTY_02: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key1',
        component: 'AssistantTemplates',
      },
      step3: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
        component: 'AiAdditionalInfo',
      },
      step4: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
        component: 'ReactionSpeed',
      },
    },
    KITTY_03: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key4',
        component: 'AssistantTemplates',
      },
      step3: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
        component: 'AiAdditionalInfo',
      },
      step4: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key6',
        component: 'ReactionSpeed',
      },
    },
    KITTY_04: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key10',
        component: 'PlugAndPlayDomain',
      },
      step3: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key14',
        component: 'PlugAndPlayDomainLinks',
      },
      step4: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key11',
        component: 'PlugAndPlayAutoUpdate',
      },
    },
    MCBOT_01: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key8',
        component: 'TextScenarioTemplates',
      },
    },
    VIDEO_SCENARIO: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key9',
        component: 'VideoScenarioTemplates',
      },
    },
    CLONE_01: {
      step2: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
        component: 'AiAdditionalInfo',
      },
      step3: {
        breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
        component: 'ReactionSpeed',
      },
    },
  },
};

const botSlice = createSlice({
  name: 'bots',
  initialState,
  reducers: {
    handleResetWizard: state => {
      state.wizard = {
        ...initialState.wizard,
        id: state.wizard.id,
      };
    },

    handleToggleWizardModal: (state, action: PayloadAction<{ status: boolean; category?: 'widget' | 'clone' }>) => {
      state.wizard.showModal = action.payload.status;
      action.payload.category && (state.wizard.category = action.payload.category);

      if (action.payload.category) {
        state.wizard.bot_type = action.payload.category === 'widget' ? BotCategoryEnum.SCENARIO_BASED_CHATBOT : BotCategoryEnum.AI_BASED_CHATBOT;
        state.wizard.bot_code = action.payload.category === 'widget' ? BotTypeEnum.TEXT_SCENARIO : BotTypeEnum.HUMAN_CLONE;

        state.wizard.bot_config = {
          ...{
            ...initialBotConfig[action.payload.category === 'widget' ? BotCategoryEnum.SCENARIO_BASED_CHATBOT : (BotCategoryEnum.AI_BASED_CHATBOT as BotType)],
            assistant:
              (action.payload.category === 'widget' ? BotTypeEnum.TEXT_SCENARIO : (BotTypeEnum.HUMAN_CLONE as BotCode)) === BotTypeEnum.HUMAN_CLONE
                ? 'self_clone'
                : '',
          },
          bot_code: action.payload.category === 'widget' ? BotTypeEnum.TEXT_SCENARIO : (BotTypeEnum.HUMAN_CLONE as BotCode),
          title: '',
        };
        state.wizard.bot_ui_info = {
          currentStep: 1,
          wizard: initialWizardConfig['create'][action.payload.category === 'widget' ? BotTypeEnum.TEXT_SCENARIO : (BotTypeEnum.HUMAN_CLONE as BotCode)],
        };
      }
    },

    handleInitializeWizard: (
      state,
      action: PayloadAction<{
        bot_code: string;
        bot_type: string;
        title?: string;
        cloneExtra?: AIKittyType['cloneExtra'];
      }>
    ) => {
      state.wizard = {
        ...state.wizard,
        id: '',
        bot_code: action.payload.bot_code as BotCode,
        bot_type: action.payload.bot_type as BotType,
        bot_config: {
          ...{
            ...initialBotConfig[action.payload.bot_type as BotType],
            assistant:
              (action.payload.bot_code as BotCode) === BotTypeEnum.HUMAN_CLONE
                ? 'self_clone'
                : (action.payload.bot_code as BotCode) === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY
                ? 'customer_support'
                : '',
            cloneExtra: action.payload.cloneExtra ?? undefined,
          },
          bot_code: action.payload.bot_code as BotCode,
          title: action.payload.title || state.wizard.bot_config?.title || initialBotConfig[action.payload.bot_type as BotType].title,
        },
        bot_ui_info: {
          currentStep: 1,
          wizard:
            (action.payload.bot_code as BotCode) !== BotTypeEnum.HUMAN_CLONE
              ? initialWizardConfig['create'][action.payload.bot_code as BotCode]
              : action.payload.cloneExtra?.template === 'basic'
              ? {
                  step2: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
                    component: 'AiAdditionalInfo',
                  },
                  step3: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
                    component: 'ReactionSpeed',
                  },
                }
              : action.payload.cloneExtra?.template === 'clonePlusDescription'
              ? {
                  step2: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
                    component: 'AiAdditionalInfo',
                  },
                  step3: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key12',
                    component: 'CloneExtraDescription',
                  },
                  step4: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
                    component: 'ReactionSpeed',
                  },
                }
              : {
                  step2: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
                    component: 'AiAdditionalInfo',
                  },
                  step3: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key13',
                    component: 'CloneExtraVideo',
                  },
                  step4: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
                    component: 'ReactionSpeed',
                  },
                },
        },
        isInEditMode: false,
      };
    },

    handleModifyBotName: (state, action: PayloadAction<string>) => {
      if (state.wizard?.bot_config) state.wizard.bot_config.title = action.payload;
    },

    handleSetTemplateId: (state, action: PayloadAction<number>) => {
      if (state.wizard?.bot_config) (state.wizard.bot_config as McBotFaceType).template_id = action.payload;
    },

    handleSetAssistantType: (state, action: PayloadAction<string>) => {
      if (state.wizard?.bot_config) {
        (state.wizard.bot_config as AIKittyType).assistant = action.payload;
        (state.wizard.bot_config as AIKittyType).bot_code = state.wizard.bot_code;
        (state.wizard.bot_config as AIKittyType).instructions = {};
      }
    },

    handleSetBasicAdditionalInfos: (
      state,
      action: PayloadAction<{
        welcoming: string;
        additional_instructions: string;
      }>
    ) => {
      (state.wizard.bot_config as AIKittyType).welcoming = action.payload.welcoming;
      (state.wizard.bot_config as AIKittyType).additional_instructions =
        action.payload.additional_instructions === '' ? [] : [action.payload.additional_instructions];
    },

    handleSetExtraAdditionalInfos: (
      state,
      action: PayloadAction<{
        agent_name?: string;
        company_name?: string;
        domain?: string;
        personality?: string;
        domains?: string;
        legal_sector?: string;
        business_sector?: string;
        disclaimer?: string;
        products_services?: string;
        brand?: string;
        target_audience?: string;
        subject_expertise?: string;
        education_level?: string;
        tech_specialization?: string;
        specialization?: string;
        industry_focus?: string;
        market_area?: string;
        establishment_name?: string;
        cuisine_type?: string;
        event_specialization?: string;
        agency_name?: string;
      }>
    ) => {
      (state.wizard.bot_config as AIKittyType).instructions = {
        ...action.payload,
      };
    },

    handleSetAiSpeedType: (state, action: PayloadAction<AIKittyType['configuration']>) => {
      (state.wizard.bot_config as AIKittyType).configuration = action.payload;
    },

    handleSetAiKnowledgeFiles: (state, action: PayloadAction<CdnFileType[]>) => {
      (state.wizard.bot_config as AIKittyType).files = action.payload;
    },

    handleInitializeEditWizard: (
      state,
      action: PayloadAction<{
        id: string;
        bot_code: BotCode;
        bot_type: BotType;
        bot_config: McBotFaceType | AIKittyType;
        showModal?: boolean;
        title?: string;
        status?: string;
      }>
    ) => {
      state.wizard = {
        title: action.payload.title,
        category: action.payload.bot_code === BotTypeEnum.HUMAN_CLONE ? 'clone' : 'widget',
        showModal: action.payload.showModal === undefined ? true : action.payload.showModal,
        isInEditMode: true,
        bot_ui_info: {
          currentStep: 1,
          wizard:
            (action.payload.bot_code as BotCode) !== BotTypeEnum.HUMAN_CLONE
              ? initialWizardConfig['edit'][action.payload.bot_code as BotCode]
              : (action.payload.bot_config as AIKittyType).cloneExtra?.template === 'basic'
              ? {
                  step2: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
                    component: 'AiAdditionalInfo',
                  },
                  step3: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
                    component: 'ReactionSpeed',
                  },
                  step4: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key5',
                    component: 'KnowledgeSeeder',
                  },
                }
              : (action.payload.bot_config as AIKittyType).cloneExtra?.template === 'clonePlusDescription'
              ? {
                  step2: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
                    component: 'AiAdditionalInfo',
                  },
                  step3: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key12',
                    component: 'CloneExtraDescription',
                  },
                  step4: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
                    component: 'ReactionSpeed',
                  },
                  step5: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key5',
                    component: 'KnowledgeSeeder',
                  },
                }
              : {
                  step2: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key2',
                    component: 'AiAdditionalInfo',
                  },
                  step3: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key13',
                    component: 'CloneExtraVideo',
                  },
                  step4: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key3',
                    component: 'ReactionSpeed',
                  },
                  step5: {
                    breadCrumbI18nKey: 'Wizard/breadcrumbs/key5',
                    component: 'KnowledgeSeeder',
                  },
                },
        },
        id: action.payload.id,
        bot_code: action.payload.bot_code,
        bot_type: action.payload.bot_type,
        bot_config: action.payload.bot_config,
        status: action.payload.status,
      };
    },

    handleToggleBotsIntegrationModal: (
      state,
      action: PayloadAction<{
        status: boolean;
        extra?: { botId: string; botCode: BotCode; title: string };
      }>
    ) => {
      state.ui.showIntegrationModal = action.payload.status;
      state.ui.integrationModalData = action.payload.extra && action.payload.status ? action.payload.extra : { botId: '', botCode: null, title: '' };
    },

    handleToggleCloneShareModal: (
      state,
      action: PayloadAction<{
        status: boolean;
        data?: { cloneId: string; logo: string };
      }>
    ) => {
      state.ui.showCloneShareModal = action.payload.status;
      state.ui.cloneShareModalData =
        action.payload.data && action.payload.status
          ? {
              cloneId: action.payload.data.cloneId,
              logo: action.payload.data.logo ?? 'https://cdn.1cdn.app/application/LOUISEBOT/23091707385795_Logo_colorful.svg',
            }
          : { cloneId: '', logo: '' };
    },

    handleRemoveTargetKnowledgeFile: (state, action: PayloadAction<string>) => {
      (state.wizard.bot_config as AIKittyType).files = (state.wizard.bot_config as AIKittyType).files?.filter(file => file.id !== action.payload);
    },

    handleUpdateCurrentStep: (state, action: PayloadAction<number>) => {
      state.wizard.bot_ui_info.currentStep = action.payload;
    },

    handleSetPlugAndPlayInfos: (
      state,
      action: PayloadAction<{
        address?: string;
        auto_update?: string;
        links?: string[];
      }>
    ) => {
      (state.wizard.bot_config as AIKittyType).plug_and_play = {
        auto_update: action.payload.auto_update || (state.wizard.bot_config as AIKittyType).plug_and_play?.auto_update || '',
        address: action.payload.address || (state.wizard.bot_config as AIKittyType).plug_and_play?.address || '',
        links: action.payload.links || (state.wizard.bot_config as AIKittyType).plug_and_play?.links || [],
      };
    },

    handleSetCloneExtra: (
      state,
      action: PayloadAction<{
        videoBotId?: string;
        description?: {
          name: string;
          profession: string;
          bio: string;
        };
        externalLinks: Array<{
          logo: string;
          link: string;
        }>;
      }>
    ) => {
      (state.wizard.bot_config as AIKittyType).cloneExtra = {
        template: (state.wizard.bot_config as AIKittyType).cloneExtra?.template || 'basic',
        data:
          (state.wizard.bot_config as AIKittyType).cloneExtra?.template !== 'basic'
            ? {
                botId: action.payload.videoBotId ?? undefined,
                name: action.payload.description?.name ?? undefined,
                profession:
                  (state.wizard.bot_config as AIKittyType).cloneExtra?.template &&
                  (state.wizard.bot_config as AIKittyType).cloneExtra?.template === 'clonePlusDescription'
                    ? action.payload.description?.profession ?? ''
                    : undefined,
                bio: action.payload.description?.bio ?? undefined,
              }
            : undefined,
        externalLinks:
          (state.wizard.bot_config as AIKittyType).cloneExtra?.template && (state.wizard.bot_config as AIKittyType).cloneExtra?.template !== 'basic'
            ? action.payload.externalLinks || (state.wizard.bot_config as AIKittyType).cloneExtra?.externalLinks || []
            : [],
      };
    },

    handleSetStepExtraData: (
      state,
      action: PayloadAction<{
        links: any;
        oldDomain: string;
        isNewRequest: boolean;
      }>
    ) => {
      if (state.wizard?.bot_ui_info?.wizard) {
        const currentStep = state.wizard.bot_ui_info.currentStep;
        const stepKey = `step${currentStep}`;

        if (stepKey in state.wizard.bot_ui_info.wizard) {
          state.wizard.bot_ui_info.wizard[stepKey].extraData = {
            links: action.payload.links,
            oldDomain: action.payload.oldDomain,
            isNewRequest: action.payload.isNewRequest,
          };
        } else {
          console.warn(`Step ${currentStep} not found in wizard`);
        }
      } else {
        console.warn('Wizard or bot_ui_info is not initialized');
      }
    },

    handleSetLivePreviewTargetBot: (state, action: PayloadAction<string>) => {
      state.ui.livePreviewTargetBot = action.payload;
    },

    handleSetCloneTemplate: (state, action: PayloadAction<'basic' | 'clonePlusDescription' | 'clonePlusVideo'>) => {
      const clone = (state.wizard.bot_config as AIKittyType).cloneExtra;
      if (clone) {
        clone.template = action.payload || 'basic';
      }
    },

    handleSetBotStatus: (state, action: PayloadAction<string>) => {
      state.wizard.status = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addMatcher(ChatbotRTKService.endpoints.getBots.matchFulfilled, (state, { payload }) => {
      const copy = [...payload];

      const clone: BotVersion = copy.find(bot => bot.code === BotTypeEnum.HUMAN_CLONE);

      if (clone) {
        clone.has_templates = true;
        clone.templates = [
          {
            id: 'basic',
            title: { en: 'Only Clone', fr: 'Juste le clone' },
            description: { en: 'Only Clone', fr: 'Juste le clone' },
            image: 'https://cdn.1cdn.app/application/LOUISEBOT/25010916542584_Simple_Clone.png',
          },
          {
            id: 'clonePlusDescription',
            title: { en: 'Clone + Bio', fr: 'Clone + Bio' },
            description: {
              en: 'Clone + Bio',
              fr: 'Clone + Bio',
            },
            image: 'https://cdn.1cdn.app/application/LOUISEBOT/25010916542584_Clone_with_Description.png',
          },
          {
            id: 'clonePlusVideo',
            title: { en: 'Clone + Chatbot', fr: 'Clone + Chatbot' },
            description: { en: 'Clone + Chatbot', fr: 'Clone + Chatbot' },

            image: 'https://cdn.1cdn.app/application/LOUISEBOT/2501091654259_Clone_with_Video_Pop_Up.png',
          },
        ];
      }

      versionAdapter.setAll(state.versions, copy);
    });

    builder.addMatcher(ChatbotRTKService.endpoints.getAssistants.matchFulfilled, (state, { payload }) => {
      assistantAdapter.setAll(state.assistants, payload);
    });

    builder.addMatcher(ScenarioRTKService.endpoints.createNewScenario.matchFulfilled, (state, { payload }) => {
      state.wizard.id = payload.id;
      state.wizard.isInEditMode = true;
    });

    builder.addMatcher(ChatbotRTKService.endpoints.createAssistantBot.matchFulfilled, (state, { payload }) => {
      state.wizard.id = payload.id;
      state.wizard.isInEditMode = true;
    });

    builder.addMatcher(ChatbotRTKService.endpoints.createKbAssistantBot.matchFulfilled, (state, { payload }) => {
      state.wizard.id = payload.id;

      if (payload.type !== BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY) state.wizard.isInEditMode = true;
    });
  },
});

//=================> Versions selectors

export const versionSelectors = versionAdapter.getSelectors((state: RootState) => state.bots.versions);

//=================> Assistants selectors
export const assistantSelectors = assistantAdapter.getSelectors((state: RootState) => state.bots.assistants);

//=================> ClientBots selectors
// export const clientBotSelectors = botAdapter.getSelectors(
//   (state: RootState) => state.bots.clientBots
// );

//=================> Wizard selectors
export const wizardState = (state: RootState) => state.bots.wizard;

//=================> Ui selectors
export const botsUiState = (state: RootState) => state.bots.ui;

//===============> Actions
export const {
  handleResetWizard,
  handleToggleWizardModal,
  handleInitializeWizard,
  handleModifyBotName,
  handleSetTemplateId,
  handleSetAssistantType,
  handleSetBasicAdditionalInfos,
  handleSetExtraAdditionalInfos,
  handleSetAiSpeedType,
  handleSetAiKnowledgeFiles,
  handleInitializeEditWizard,
  handleToggleBotsIntegrationModal,
  handleToggleCloneShareModal,
  handleRemoveTargetKnowledgeFile,
  handleUpdateCurrentStep,
  handleSetPlugAndPlayInfos,
  handleSetCloneExtra,
  handleSetStepExtraData,
  handleSetLivePreviewTargetBot,
  handleSetCloneTemplate,
  handleSetBotStatus,
} = botSlice.actions;

export default botSlice.reducer;
